/**
 * etichette — traduce i valori "da codice" del backend in testo leggibile.
 *
 * Andrea 2026-09-08: in giro per l'app comparivano valori grezzi come
 * `voti_caricati`, `champions_league`, `auto_rifiutata_budget`. Qui c'è
 * una mappa esplicita per i casi che meritano una dicitura curata, più
 * un fallback che rende presentabile qualunque snake_case sconosciuto
 * ("da_iniziare" → "Da iniziare"): così anche i valori aggiunti in
 * futuro non finiscono mai a schermo con gli underscore.
 */

import { NOME_MODALITA_PRO } from "./nomenclatura";
import { tr } from "../../i18n";

const ETICHETTE: Record<string, string> = {
  // — Stati generici (giornate, stagioni, competizioni, partite, finestre) —
  da_iniziare: "Da iniziare",
  da_giocare: "Da giocare",
  in_corso: "In corso",
  voti_caricati: "Voti caricati",
  voti_da_confermare: "Voti da confermare",
  calcolata: "Calcolata",
  annullata: "Annullata",
  conclusa: "Conclusa",
  archiviata: "Archiviata",
  sospesa: "Sospesa",
  rinviata: "Rinviata",
  giocata: "Giocata",
  attiva: "Attiva",
  chiusa: "Chiusa",
  aperta: "Aperta",

  // — Competizioni —
  campionato: "Campionato",
  champions_league: "Coppa",
  supercoppa: "Supercoppa",
  coppa: "Coppa",
  girone_a: "Girone A",
  girone_b: "Girone B",
  quarti_andata: "Quarti · andata",
  quarti_ritorno: "Quarti · ritorno",
  semi_andata: "Semifinale · andata",
  semi_ritorno: "Semifinale · ritorno",
  finale_andata: "Finale · andata",
  finale_ritorno: "Finale · ritorno",
  spareggio_simbolico: "Spareggio simbolico",
  sorteggio_random: "Sorteggio casuale",
  teste_di_serie_classifica_precedente: "Teste di serie (classifica precedente)",
  round_robin: "Girone all'italiana",
  round_robin_doppio: "Girone all'italiana (andata e ritorno)",
  round_robin_sestuplo_tagliato: "Girone sestuplo (tagliato)",

  // — Mercato —
  proposta: "Proposta",
  controproposta_emessa: "Controproposta emessa",
  accettata: "Accettata",
  rifiutata: "Rifiutata",
  auto_rifiutata_budget: "Auto-rifiutata (budget)",
  auto_rifiutata_giocatore: "Auto-rifiutata (giocatore)",
  acquisto: "Acquisto",
  vendita: "Vendita",
  scambio: "Scambio",
  scambio_conguaglio: "Scambio con conguaglio",
  conguaglio_scambio: "Conguaglio scambio",
  prestito_secco: "Prestito secco",
  prestito_ddr: "Prestito con diritto di riscatto",
  prestito_odr: "Prestito con obbligo di riscatto",
  prestito_odr_condizionato: "Prestito con obbligo condizionato",
  scelta_ddr: "Scelta diritto di riscatto",
  verifica_odr_condizionato: "Verifica obbligo condizionato",
  proprieta: "Proprietà",
  mercato_estivo: "Mercato estivo",
  mercato_invernale: "Mercato invernale",
  asta_svincolati: "Asta svincolati",
  acquisto_svincolato_asta: "Acquisto svincolato (asta)",
  svincolo_admin: "Svincolo (admin)",
  rettifica_prezzo: "Rettifica prezzo (admin)",
  acquisto_svincolato_registrato: "Acquisto svincolato registrato",
  chiusura_mercato_estivo: "Chiusura mercato estivo",
  chiusura_mercato_invernale: "Chiusura mercato invernale",

  // — Rose e giocatori —
  serie_a: "In Serie A",
  all_estero: "Trasferito all'estero",
  ritirato: "Ritirato",
  deceduto: "Deceduto",
  retrocesso: "Retrocesso",
  ufficiale: "Convocato",
  provvisoria: "Non convocato",
  abbandono_serie_a: "Abbandono Serie A",
  compensazione_abbandono_serie_a: "Compensazione abbandono Serie A",
  scelta_abbandono_risolta: "Scelta abbandono risolta",
  swap_rosa_effettuato: "Swap rosa effettuato",
  lista_ordinata: "Lista ordinata",
  lista_provvisoria: "Lista provvisoria",
  non_rinnova: "Non rinnovato",
  rinnova: "Rinnovato",

  // — Budget / tesoreria —
  dotazione_iniziale: "Dotazione iniziale",
  bonus_stagionale: "Bonus stagionale",
  bonus_invernale: "Bonus invernale",
  bonus_classifica: "Bonus classifica",
  multa_reale: "Multa",
  trattenuti_mensile: "Trattenuta mensile",
  rinnovo: "Rinnovo",
  penale: "Penale",
  scommessa_puntata: "Scommessa · puntata",
  scommessa_vincita: "Scommessa · vincita",
  scommessa_rimborso: "Scommessa · rimborso",

  // — Ruoli di lega —
  presidente_lega: "Presidente di Lega",
  admin: "Amministratore",
  tesoriere: "Tesoriere",
  mod_tm: "Moderatore Transfermarkt",
  mod_mercato: "Moderatore mercato",
  partecipante: "Partecipante",
  allenatore_secondo: "Allenatore in seconda",
  ospite: "Ospite",

  // — Notifiche —
  nuova_offerta: "Nuova offerta",
  offerta_accettata: "Offerta accettata",
  offerta_rifiutata: "Offerta rifiutata",
  offerta_annullata: "Offerta annullata",
  offerta_auto_rifiutata: "Offerta auto-rifiutata",
  condizione_riscatto_verificata: "Condizione di riscatto verificata",
  giornata_calcolata: "Giornata calcolata",
  // Nomenclatura Pro (29/09): la modalità si chiama Pro
  mantra: NOME_MODALITA_PRO,
  classic: "Classic",
  gazzetta_prima_pagina: "Squad Magazine · prima pagina",
  gazzetta_report: "Squad Magazine · report",
  gazzetta_menzione: "Squad Magazine · ti hanno nominato",
  gazzetta_top11: "Squad Magazine · Top 11",
  giornata_annullata: "Giornata annullata",
  giornata_ricalcolata: "Giornata ricalcolata",
  classifica_aggiornata: "Classifica aggiornata",
  live_voti_avviato: "Voti live avviati",
  live_voti_terminato: "Voti live terminati",
  finestra_mercato_aperta: "Finestra di mercato aperta",
  finestra_mercato_chiusa: "Finestra di mercato chiusa",
  rinnovo_effettuato: "Rinnovo effettuato",
  stagione_conclusa: "Stagione conclusa",
  prestito_rientrato: "Prestito rientrato",
  probabili_aggiornate: "Probabili aggiornate",
  invito_emesso: "Invito inviato",
  invito_accettato: "Invito accettato",
  scraper_tm_completato: "Scraper Transfermarkt completato",
  scraper_tm_fallito: "Scraper Transfermarkt fallito",
  scraper_voti_completato: "Scraper voti completato",
  scraper_voti_fallito: "Scraper voti fallito",
  snapshot_evento_creato: "Snapshot valori creato",

  // — Privacy —
  pubblico: "Pubblico",
  lega: "Solo la lega",
  riservato: "Riservato",
  privato: "Privato",
  visibile: "Visibile",
  aggregato: "Solo totali",
  nascosto: "Nascosto",

  // — Voti, fonti, import —
  manuale: "Manuale",
  manuale_admin: "Manuale (admin)",
  admin_manuale: "Manuale (admin)",
  scraper: "Scraper",
  csv_manuale: "CSV manuale",
  api_football_data: "API football-data",
  on_demand_trigger: "Su richiesta",
  snapshot_evento: "Snapshot evento",
  esatto_nome: "Nome esatto",
  esatto_alias: "Alias esatto",
  fuzzy_con_squadra: "Fuzzy (con squadra)",
  fuzzy_globale: "Fuzzy (globale)",
  non_abbinato: "Non abbinato",
  marcati_obsoleti: "Marcati obsoleti",
  flag_errato_corretto: "Segnalato come errato",
  media_voto_base: "Media voto base",
  sei_politico: "Sei politico",
  sei_politico_rinvio: "Sei politico (rinvio)",
  sempre_sei_politico: "Sempre sei politico",
  sempre_rinvia: "Rinvia sempre",
  admin_decide_caso_per_caso: "L'admin decide caso per caso",
  sostituzione_ruolo_fedele: "Sostituzione a ruolo fedele",
  sostituzione_ruolo_libero: "Sostituzione a ruolo libero",
  supplementari_panchina: "Supplementari dalla panchina",
  sconfitta_automatica: "Sconfitta automatica",
  conferma_admin: "Conferma admin",
  self_service: "Self-service",
  tutti_sa_inizio_mercato: "Tutti a inizio mercato",
  chiusura_stagione: "Chiusura stagione",

  // — Tie-break e classifiche —
  fantapunti_torneo: "Fantapunti del torneo",
  fantapunti_partita: "Fantapunti della partita",
  diff_reti_torneo: "Differenza reti",
  somma_fantapunti: "Somma fantapunti",
  gol_vittoria: "Gol vittoria",
  gol_pareggio: "Gol pareggio",
  gol_portiere: "Gol del portiere",
  gol_subito_portiere: "Gol subito dal portiere",

  // — Scommesse —
  vinta_proponente: "Vinta dal proponente",
  vinta_accettante: "Vinta dall'accettante",

  // — Maglie —
  tinta_unita: "Tinta unita",
  strisce_orizzontali: "Strisce orizzontali",
  strisce_verticali: "Strisce verticali",
  meta_verticale: "Metà verticale",
  maniche_contrasto: "Maniche a contrasto",

  // — Regolamento (eventi bonus/malus, comportamenti, spareggi) —
  gol: "Gol",
  assist: "Assist",
  ammonizione: "Ammonizione",
  espulsione: "Espulsione",
  autogoal: "Autogol",
  autogol: "Autogol",
  rigore_segnato: "Rigore segnato",
  rigore_sbagliato: "Rigore sbagliato",
  rigore_parato: "Rigore parato",
  portiere_imbattuto: "Portiere imbattuto",
  voto_base_6: "Voto base 6",
  punteggio_fisso: "Punteggio fisso",
  per_ruolo: "Per ruolo",
  piazzamento_stagione: "Piazzamento in stagione",

  // — Esiti generici —
  successo: "Successo",
  parziale: "Parziale",
  fallito: "Fallito",
  errore: "Errore",
};

/** Rende leggibile un valore enum del backend. Se sconosciuto: underscore
 * in spazi e prima lettera maiuscola. */
export function etichetta(valore: string | null | undefined): string {
  if (!valore) return "—";
  const v = String(valore);
  const nota = ETICHETTE[v];
  if (nota) return tr(nota);
  const pulito = v.replace(/_/g, " ").trim();
  return tr(pulito.charAt(0).toUpperCase() + pulito.slice(1));
}

/** Versione minuscola, per inserire l'etichetta dentro una frase. */
export function etichettaMinuscola(valore: string | null | undefined): string {
  const e = etichetta(valore);
  return e === "—" ? e : e.charAt(0).toLowerCase() + e.slice(1);
}
